import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Dashboard stats
export const getDashboardStats = async () => {
  const response = await axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() });
  return response.data;
};

// Token usage stats (admin only)
export const getTokenUsageStats = async () => {
  const response = await axios.get(`${API}/dashboard/token-usage`, { headers: getAuthHeader() });
  return response.data;
};

// Database connections
export const getConnections = async () => {
  const response = await axios.get(`${API}/connections`, { headers: getAuthHeader() });
  return response.data;
};

// Refresh all schemas - forces AI to reload latest table structures
export const refreshAllSchemas = async () => {
  const response = await axios.post(`${API}/schema/refresh-all`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const createConnection = async (data) => {
  const response = await axios.post(`${API}/connections`, data, { headers: getAuthHeader() });
  return response.data;
};

export const deleteConnection = async (id) => {
  const response = await axios.delete(`${API}/connections/${id}`, { headers: getAuthHeader() });
  return response.data;
};

export const testConnection = async (id) => {
  const response = await axios.post(`${API}/connections/${id}/test`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const discoverSchema = async (id) => {
  const response = await axios.post(`${API}/connections/${id}/discover`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const getSnapshot = async (connectionId) => {
  const response = await axios.get(`${API}/connections/${connectionId}/snapshot`, { headers: getAuthHeader() });
  return response.data;
};

export const getSchema = async (connectionId) => {
  const response = await axios.get(`${API}/connections/${connectionId}/schema`, { headers: getAuthHeader() });
  return response.data;
};

// AI queries
export const sendAIQuery = async (query, connectionId = null, conversationId = null, forceRefresh = false) => {
  const response = await axios.post(`${API}/ai/query`, { 
    query, 
    connection_id: connectionId,
    conversation_id: conversationId,
    force_refresh: forceRefresh
  }, { headers: getAuthHeader() });
  return response.data;
};

export const estimateAIQuery = async (query, connectionId = null) => {
  const response = await axios.post(`${API}/ai/estimate`, { 
    query, 
    connection_id: connectionId
  }, { headers: getAuthHeader() });
  return response.data;
};

export const confirmAIQuery = async (query, connectionId = null, conversationId = null, estimateId) => {
  const response = await axios.post(`${API}/ai/confirm`, { 
    query, 
    connection_id: connectionId,
    conversation_id: conversationId,
    estimate_id: estimateId
  }, { headers: getAuthHeader() });
  return response.data;
};

// Staged AI Query (Interactive step-by-step process)
export const stagedAIQuery = async (query, stage, options = {}) => {
  const response = await axios.post(`${API}/ai/staged-query`, {
    query,
    stage,
    connection_id: options.connectionId || null,
    conversation_id: options.conversationId || null,
    user_correction: options.userCorrection || null,
    approved_understanding: options.approvedUnderstanding || null,
    approved_plan: options.approvedPlan || null,
    clarification_answer: options.clarificationAnswer || null,
    clarification_context: options.clarificationContext || null
  }, { headers: getAuthHeader() });
  return response.data;
};

// Autonomous AI - full MongoDB access, AI figures everything out itself
export const autonomousAIQuery = async (question, conversationId = null) => {
  const response = await axios.post(`${API}/ai/autonomous-query`, {
    question,
    conversation_id: conversationId,
    max_iterations: 12
  }, { headers: getAuthHeader() });
  return response.data;
};

// Smart Two-Stage AI Query (better for large schemas)
export const smartAIQuery = async (question, conversationId = null, previewOnly = false, analystRole = 'all') => {
  const response = await axios.post(`${API}/ai/smart-query`, {
    question,
    conversation_id: conversationId,
    preview_only: previewOnly,
    analyst_role: analystRole
  }, { headers: getAuthHeader() });
  return response.data;
};

// Streaming version - uses Server-Sent Events for real-time progress
// Handles: status (progress), query_results (immediate data), analysis_complete (insights), result (final), error
export const smartAIQueryStream = (question, conversationId = null, previewOnly = false, analystRole = 'all', onProgress, onResult, onError, onQueryResults, onAnalysisComplete, onExplainAnalysis, adminTrainingMode = false, onAiThought = null) => {
  const token = localStorage.getItem('token');
  
  // Use fetch with ReadableStream for SSE
  fetch(`${API}/ai/smart-query-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      question,
      conversation_id: conversationId,
      preview_only: previewOnly || adminTrainingMode,  // Admin training mode implies preview
      admin_training_mode: adminTrainingMode,
      analyst_role: analystRole
    })
  }).then(response => {
    if (!response.ok) {
      // Handle HTTP errors (401, 500, etc.)
      response.text().then(text => {
        console.error('[SSE] HTTP error:', response.status, text);
        if (onError) {
          try {
            const errData = JSON.parse(text);
            onError({ message: errData.detail || errData.error || `Server error: ${response.status}`, type: 'error' });
          } catch {
            onError({ message: `Server error: ${response.status} - ${text.substring(0, 200)}`, type: 'error' });
          }
        }
      });
      return;
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    function processChunk() {
      reader.read().then(({ done, value }) => {
        if (done) return;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('[SSE]', data.type, data.elapsed || '', data.message || '');
              
              if (data.type === 'status' && onProgress) {
                // Progress update with elapsed time
                onProgress(data);
              } else if (data.type === 'ai_thought') {
                // Rich AI thinking event (reasoning, plan, interpretation, table explanations)
                console.log('[SSE] AI thought:', data.stage, data.label);
                if (onAiThought) onAiThought(data);
              } else if (data.type === 'query_id') {
                // Store query_id for cancellation
                window._activeStreamQueryId = data.query_id;
                console.log('[SSE] Query ID:', data.query_id);
              } else if (data.type === 'explain_analysis' && onExplainAnalysis) {
                // EXPLAIN analysis received BEFORE query results
                console.log('[SSE] EXPLAIN analysis:', data.analysis?.risk_level);
                onExplainAnalysis(data);
              } else if (data.type === 'query_results' && onQueryResults) {
                // Query results available IMMEDIATELY (before AI analysis)
                onQueryResults(data);
              } else if (data.type === 'analysis_complete' && onAnalysisComplete) {
                // AI analysis completed (may come later)
                onAnalysisComplete(data);
              } else if (data.type === 'clarification_needed') {
                // AI needs clarification for vague queries
                console.log('[SSE] Clarification needed:', data.question);
                if (onResult) {
                  onResult({
                    success: true,
                    needs_clarification: true,
                    clarification_question: data.question,
                    question_for_user: data.question,  // Also pass as question_for_user
                    suggested_queries: data.suggested_queries || [],
                    reasoning: data.reasoning || '',
                    partial_understanding: data.partial_understanding || data.reasoning || '',
                    clarification_context: data.clarification_context || {}
                  });
                }
              } else if (data.type === 'table_clarification_needed') {
                // ACTIVE LEARNING — AI is unsure which table for an ambiguous term
                console.log('[SSE] Table clarification needed:', data.ambiguous_term, data.candidates);
                if (onResult) {
                  onResult({
                    success: true,
                    needs_table_clarification: true,
                    ambiguous_term: data.ambiguous_term || '',
                    clarification_question: data.question || '',
                    candidates: data.candidates || [],
                    original_question: data.original_question || '',
                    reasoning: data.reasoning || '',
                  });
                }
              } else if (data.type === 'conversational_response') {
                // Meta / capability / greeting question — backend bypassed the SQL pipeline
                console.log('[SSE] Conversational response received');
                if (onResult) {
                  onResult({
                    success: true,
                    is_conversational: true,
                    content: data.content || '',
                  });
                }
              } else if (data.type === 'result' && onResult) {
                // Final complete result
                onResult(data.data);
              } else if (data.type === 'error' && onError) {
                // Pass the full error object including sql_attempted
                onError(data);
              }
            } catch (e) {
              console.error('SSE parse error:', e, line);
            }
          }
        }
        
        processChunk();
      });
    }
    
    processChunk();
  }).catch(err => {
    if (onError) onError(err.message);
  });
};

// Get analyst roles configuration
export const getAnalystRoles = async () => {
  const response = await axios.get(`${API}/analyst-roles`, { headers: getAuthHeader() });
  return response.data;
};

// Save comprehensive learning across all knowledge stores
export const saveComprehensiveLearning = async (learningData) => {
  const response = await axios.post(`${API}/learning/save-comprehensive`, learningData, { headers: getAuthHeader() });
  return response.data;
};

// Autonomous Investigation Agent - runs diagnostic queries and finds root cause
export const runInvestigation = async (investigationData) => {
  const response = await axios.post(`${API}/ai/investigate`, investigationData, { headers: getAuthHeader() });
  return response.data;
};

// Admin Training Mode - Preview query without execution
export const previewSmartQuery = async (question, conversationId = null, analystRole = 'all') => {
  const response = await axios.post(`${API}/ai/smart-query-preview`, {
    question,
    conversation_id: conversationId,
    analyst_role: analystRole
  }, { headers: getAuthHeader() });
  return response.data;
};

// Execute a confirmed query after admin review
export const executeConfirmedQuery = async (server, database, sql, question, conversationId = null, adminFeedback = null) => {
  const response = await axios.post(`${API}/ai/execute-confirmed-query`, {
    server,
    database,
    sql,
    question,
    conversation_id: conversationId,
    admin_feedback: adminFeedback
  }, { headers: getAuthHeader() });
  return response.data;
};

export const getQueryHistory = async (limit = 100) => {
  const response = await axios.get(`${API}/ai/history?limit=${limit}`, { headers: getAuthHeader() });
  return response.data;
};

export const getConversation = async (conversationId) => {
  const response = await axios.get(`${API}/ai/conversation/${conversationId}`, { headers: getAuthHeader() });
  return response.data;
};

export const deleteConversation = async (conversationId) => {
  const response = await axios.delete(`${API}/ai/conversation/${conversationId}`, { headers: getAuthHeader() });
  return response.data;
};

export const deleteQuery = async (id) => {
  const response = await axios.delete(`${API}/ai/history/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// User management
export const getUsers = async () => {
  const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
  return response.data;
};

export const createUser = async (data) => {
  const response = await axios.post(`${API}/users`, data, { headers: getAuthHeader() });
  return response.data;
};

export const updateUserRole = async (userId, role) => {
  const response = await axios.put(`${API}/users/${userId}/role`, { role }, { headers: getAuthHeader() });
  return response.data;
};

export const resetUserPassword = async (userId, newPassword) => {
  const response = await axios.put(`${API}/users/${userId}/password`, { new_password: newPassword }, { headers: getAuthHeader() });
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeader() });
  return response.data;
};

// Password change
export const changePassword = async (currentPassword, newPassword) => {
  const response = await axios.put(`${API}/auth/password`, {
    current_password: currentPassword,
    new_password: newPassword
  }, { headers: getAuthHeader() });
  return response.data;
};

// 2FA
export const setup2FA = async () => {
  const response = await axios.post(`${API}/auth/2fa/setup`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const verify2FASetup = async (code) => {
  const response = await axios.post(`${API}/auth/2fa/verify`, { code }, { headers: getAuthHeader() });
  return response.data;
};

export const validate2FA = async (code) => {
  const response = await axios.post(`${API}/auth/2fa/validate`, { code }, { headers: getAuthHeader() });
  return response.data;
};

// IP Whitelisting / Security
export const getWhitelist = async () => {
  const response = await axios.get(`${API}/security/whitelist`, { headers: getAuthHeader() });
  return response.data;
};

export const addToWhitelist = async (ip, description = '') => {
  const response = await axios.post(`${API}/security/whitelist`, { ip, description }, { headers: getAuthHeader() });
  return response.data;
};

export const removeFromWhitelist = async (ip) => {
  const response = await axios.delete(`${API}/security/whitelist/${ip}`, { headers: getAuthHeader() });
  return response.data;
};

// Knowledge base / Learning
export const getKnowledgeEntries = async (connectionId = null, category = null) => {
  let url = `${API}/learning/entries`;
  const params = [];
  if (connectionId) params.push(`connection_id=${connectionId}`);
  if (category) params.push(`category=${category}`);
  if (params.length) url += '?' + params.join('&');
  const response = await axios.get(url, { headers: getAuthHeader() });
  return response.data;
};

export const createKnowledgeEntry = async (data) => {
  const response = await axios.post(`${API}/learning/entries`, data, { headers: getAuthHeader() });
  return response.data;
};

export const updateKnowledgeEntry = async (entryId, answer) => {
  const response = await axios.put(`${API}/learning/entries/${entryId}`, { answer }, { headers: getAuthHeader() });
  return response.data;
};

export const deleteKnowledgeEntry = async (entryId) => {
  const response = await axios.delete(`${API}/learning/entries/${entryId}`, { headers: getAuthHeader() });
  return response.data;
};

export const askAILearningQuestion = async (connectionId = null) => {
  const response = await axios.post(`${API}/learning/ask-ai`, { connection_id: connectionId }, { headers: getAuthHeader() });
  return response.data;
};

// AI Onboarding - Persistent Learning System
export const getLearningStatus = async () => {
  const response = await axios.get(`${API}/learning/status`, { headers: getAuthHeader() });
  return response.data;
};

export const generateLearningQuestions = async (count = 1) => {
  const response = await axios.post(`${API}/learning/generate-questions`, { count }, { headers: getAuthHeader() });
  return response.data;
};

export const resetPendingQuestions = async () => {
  const response = await axios.post(`${API}/learning/reset-pending`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const saveLearningPriorities = async (databases) => {
  const response = await axios.post(`${API}/learning/priorities`, { databases }, { headers: getAuthHeader() });
  return response.data;
};

export const getLearningPriorities = async () => {
  const response = await axios.get(`${API}/learning/priorities`, { headers: getAuthHeader() });
  return response.data;
};

export const getNextQuestion = async () => {
  const response = await axios.get(`${API}/learning/next-question`, { headers: getAuthHeader() });
  return response.data;
};

export const answerQuestion = async (questionId, answer) => {
  const response = await axios.post(`${API}/learning/answer`, { question_id: questionId, answer }, { headers: getAuthHeader() });
  return response.data;
};

export const skipQuestion = async (questionId) => {
  const response = await axios.post(`${API}/learning/skip-question?question_id=${questionId}`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const getSkippedQuestions = async () => {
  const response = await axios.get(`${API}/learning/skipped-questions`, { headers: getAuthHeader() });
  return response.data;
};

export const unskipQuestion = async (questionId) => {
  const response = await axios.post(`${API}/learning/unskip-question?question_id=${questionId}`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const getSystemOverview = async () => {
  const response = await axios.get(`${API}/learning/system-overview`, { headers: getAuthHeader() });
  return response.data;
};

// DBA Queries
export const getDBAQueries = async () => {
  const response = await axios.get(`${API}/learning/dba-queries`, { headers: getAuthHeader() });
  return response.data;
};

export const saveDBAQuery = async (queryData) => {
  const response = await axios.post(`${API}/learning/dba-query`, queryData, { headers: getAuthHeader() });
  return response.data;
};

export const updateDBAQuery = async (queryId, queryData) => {
  const response = await axios.put(`${API}/learning/dba-query/${queryId}`, queryData, { headers: getAuthHeader() });
  return response.data;
};

export const deleteDBAQuery = async (queryId) => {
  const response = await axios.delete(`${API}/learning/dba-query/${queryId}`, { headers: getAuthHeader() });
  return response.data;
};

export const syncKnowledgeFromDBAQuery = async (queryId) => {
  const response = await axios.post(`${API}/learning/sync-from-dba-query/${queryId}`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Knowledge Base Search
export const searchKnowledgeBase = async (query, types = null) => {
  const params = new URLSearchParams({ q: query });
  if (types) params.append('types', types);
  const response = await axios.get(`${API}/learning/search?${params}`, { headers: getAuthHeader() });
  return response.data;
};

// Export endpoints
export const createExport = async (queryId, data, queryText, format = 'xlsx') => {
  const response = await axios.post(`${API}/exports/create`, {
    query_id: queryId,
    data: data,
    query_text: queryText,
    format: format
  }, { headers: getAuthHeader() });
  return response.data;
};

export const downloadExcelDirect = async (data, filename = 'export', format = 'xlsx') => {
  const response = await axios.post(`${API}/exports/download`, {
    data,
    filename,
    format
  }, { headers: getAuthHeader(), responseType: 'blob' });
  
  // Trigger browser download
  const blob = new Blob([response.data], { 
    type: format === 'xlsx' 
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      : 'text/csv' 
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const getExports = async () => {
  const response = await axios.get(`${API}/exports`, { headers: getAuthHeader() });
  return response.data;
};

export const deleteExport = async (filename) => {
  const response = await axios.delete(`${API}/exports/${filename}`, { headers: getAuthHeader() });
  return response.data;
};

// Background query endpoints
export const submitBackgroundQuery = async (query, connectionId = null, conversationId = null) => {
  const response = await axios.post(`${API}/ai/query/background`, {
    query,
    connection_id: connectionId,
    conversation_id: conversationId
  }, { headers: getAuthHeader() });
  return response.data;
};

export const getQueryStatus = async (queryId) => {
  const response = await axios.get(`${API}/ai/query/status/${queryId}`, { headers: getAuthHeader() });
  return response.data;
};

export const getPendingQueries = async () => {
  const response = await axios.get(`${API}/ai/query/pending`, { headers: getAuthHeader() });
  return response.data;
};

export const cancelQuery = async (queryId) => {
  const response = await axios.post(`${API}/ai/query/cancel/${queryId}`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const acknowledgeQueryResult = async (queryId) => {
  const response = await axios.post(`${API}/ai/query/acknowledge/${queryId}`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const submitAIFeedback = async (queryId, originalQuery, originalSql, correction, connectionId = null) => {
  const response = await axios.post(`${API}/ai/feedback`, {
    query_id: queryId ? String(queryId) : null,
    original_query: originalQuery || '',
    original_sql: originalSql || null,
    correction: correction || '',
    connection_id: connectionId || null
  }, { headers: getAuthHeader() });
  return response.data;
};

// Quick feedback (thumbs up/down)
export const submitQuickFeedback = async (queryId, originalQuery, sql, feedbackType, feedbackReason = null) => {
  const response = await axios.post(`${API}/ai/quick-feedback`, {
    query_id: queryId,
    original_query: originalQuery,
    sql: sql,
    feedback_type: feedbackType,
    feedback_reason: feedbackReason
  }, { headers: getAuthHeader() });
  return response.data;
};

// Get AI feedback statistics
export const getAIFeedbackStats = async () => {
  const response = await axios.get(`${API}/ai/feedback-stats`, { headers: getAuthHeader() });
  return response.data;
};

// Retry AI Analysis for failed analysis
export const retryAIAnalysis = async (originalQuestion, sql, results, analystRole = 'data_scientist') => {
  const response = await axios.post(`${API}/ai/retry-analysis`, {
    original_question: originalQuestion,
    sql: sql,
    results: results,
    analyst_role: analystRole
  }, { headers: getAuthHeader() });
  return response.data;
};

// Dashboard endpoints
export const getDashboardLayout = async () => {
  const response = await axios.get(`${API}/dashboard/layout`, { headers: getAuthHeader() });
  return response.data;
};

export const saveDashboardLayout = async (widgets) => {
  const response = await axios.put(`${API}/dashboard/layout`, { widgets }, { headers: getAuthHeader() });
  return response.data;
};

export const addDashboardWidget = async (widget) => {
  const response = await axios.post(`${API}/dashboard/widgets`, widget, { headers: getAuthHeader() });
  return response.data;
};

export const removeDashboardWidget = async (widgetId) => {
  const response = await axios.delete(`${API}/dashboard/widgets/${widgetId}`, { headers: getAuthHeader() });
  return response.data;
};

// Saved Reports endpoints
export const getSavedReports = async () => {
  const response = await axios.get(`${API}/reports`, { headers: getAuthHeader() });
  return response.data;
};

export const createSavedReport = async (name, query, connectionId = null, schedule = null, visualizationType = null) => {
  const response = await axios.post(`${API}/reports`, {
    name,
    query,
    connection_id: connectionId,
    schedule,
    visualization_type: visualizationType
  }, { headers: getAuthHeader() });
  return response.data;
};

export const deleteSavedReport = async (reportId) => {
  const response = await axios.delete(`${API}/reports/${reportId}`, { headers: getAuthHeader() });
  return response.data;
};

export const runSavedReport = async (reportId) => {
  const response = await axios.post(`${API}/reports/${reportId}/run`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const pinReportToDashboard = async (reportId) => {
  const response = await axios.post(`${API}/reports/${reportId}/pin`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Admin Knowledge Approval APIs
export const getPendingSuggestions = async () => {
  const response = await axios.get(`${API}/learning/pending-suggestions`, { headers: getAuthHeader() });
  return response.data;
};

export const reviewSuggestion = async (entryId, action, adminNote = null, correctedAnswer = null) => {
  const response = await axios.post(`${API}/learning/review-suggestion`, {
    entry_id: entryId,
    action: action, // 'approve' or 'reject'
    admin_note: adminNote,
    corrected_answer: correctedAnswer
  }, { headers: getAuthHeader() });
  return response.data;
};

export const getKnowledgeStats = async () => {
  const response = await axios.get(`${API}/learning/knowledge-stats`, { headers: getAuthHeader() });
  return response.data;
};

export const saveClarificationAnswer = async (answer, pendingQuestionId = null, conversationId = null) => {
  const response = await axios.post(`${API}/learning/save-clarification`, {
    answer: answer,
    pending_question_id: pendingQuestionId,
    conversation_id: conversationId
  }, { headers: getAuthHeader() });
  return response.data;
};

// Business Setup APIs
export const getBusinessOverview = async () => {
  const response = await axios.get(`${API}/learning/business-overview`, { headers: getAuthHeader() });
  return response.data;
};

export const saveBusinessOverview = async (data) => {
  const response = await axios.post(`${API}/learning/business-overview`, data, { headers: getAuthHeader() });
  return response.data;
};

export const saveServerDescription = async (serverId, description, purpose, extras = {}) => {
  const response = await axios.post(`${API}/learning/server-description`, {
    server_id: serverId,
    description: description,
    purpose: purpose,
    ...extras
  }, { headers: getAuthHeader() });
  return response.data;
};

export const getServerDescriptions = async () => {
  const response = await axios.get(`${API}/learning/server-descriptions`, { headers: getAuthHeader() });
  return response.data;
};

export const saveTableRelationship = async (relationship) => {
  const response = await axios.post(`${API}/learning/table-relationship`, relationship, { headers: getAuthHeader() });
  return response.data;
};

export const getTableRelationships = async () => {
  const response = await axios.get(`${API}/learning/table-relationships`, { headers: getAuthHeader() });
  return response.data;
};

// Schema structure for dropdowns
export const getSchemaStructure = async () => {
  const response = await axios.get(`${API}/learning/schema-structure`, { headers: getAuthHeader() });
  return response.data;
};

// AI-powered relationship discovery
export const discoverRelationships = async () => {
  const response = await axios.post(`${API}/learning/discover-relationships`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Bulk save relationships
export const bulkSaveRelationships = async (relationships) => {
  const response = await axios.post(`${API}/learning/bulk-save-relationships`, { relationships }, { headers: getAuthHeader() });
  return response.data;
};

// Delete relationship
export const deleteTableRelationship = async (relationshipId) => {
  const response = await axios.delete(`${API}/learning/table-relationship/${relationshipId}`, { headers: getAuthHeader() });
  return response.data;
};

// Table Definitions APIs
export const getTableDefinitions = async () => {
  const response = await axios.get(`${API}/learning/table-definitions`, { headers: getAuthHeader() });
  return response.data;
};

export const getNextTableForDefinition = async () => {
  const response = await axios.get(`${API}/learning/next-table`, { headers: getAuthHeader() });
  return response.data;
};

export const saveTableDefinition = async (server, database, table, definition) => {
  const response = await axios.post(`${API}/learning/table-definition`, {
    server,
    database,
    table,
    definition
  }, { headers: getAuthHeader() });
  return response.data;
};

export const deleteTableDefinition = async (definitionId) => {
  const response = await axios.delete(`${API}/learning/table-definition/${definitionId}`, { headers: getAuthHeader() });
  return response.data;
};

// Skipped Tables API
export const skipTable = async (server, database, table) => {
  const response = await axios.post(`${API}/learning/skip-table`, {
    server,
    database,
    table
  }, { headers: getAuthHeader() });
  return response.data;
};

export const getSkippedTables = async () => {
  const response = await axios.get(`${API}/learning/skipped-tables`, { headers: getAuthHeader() });
  return response.data;
};

export const unskipTable = async (server, database, table) => {
  const response = await axios.delete(`${API}/learning/skip-table/${encodeURIComponent(server)}/${encodeURIComponent(database)}/${encodeURIComponent(table)}`, { headers: getAuthHeader() });
  return response.data;
};

// Ignored Tables API (tables permanently excluded from AI analysis)
export const ignoreTable = async (server, database, table, reason) => {
  const response = await axios.post(`${API}/learning/ignore-table`, {
    server,
    database,
    table,
    reason
  }, { headers: getAuthHeader() });
  return response.data;
};

export const getIgnoredTables = async () => {
  const response = await axios.get(`${API}/learning/ignored-tables`, { headers: getAuthHeader() });
  return response.data;
};

export const unignoreTable = async (server, database, table) => {
  const response = await axios.delete(`${API}/learning/ignore-table/${encodeURIComponent(server)}/${encodeURIComponent(database)}/${encodeURIComponent(table)}`, { headers: getAuthHeader() });
  return response.data;
};

// Scan and Bulk Migrate to Ignored
export const scanForIgnoredTables = async () => {
  const response = await axios.get(`${API}/learning/scan-for-ignored`, { headers: getAuthHeader() });
  return response.data;
};

export const bulkIgnoreTables = async (tables) => {
  const response = await axios.post(`${API}/learning/bulk-ignore`, { tables }, { headers: getAuthHeader() });
  return response.data;
};

export const aiSuggestTableDefinition = async (server, database, table, columns, rowCount) => {
  const response = await axios.post(`${API}/learning/ai-suggest-definition`, {
    server,
    database,
    table,
    columns,
    row_count: rowCount
  }, { headers: getAuthHeader() });
  return response.data;
};


// AI Key Column Discovery for Relationship Wizard
export const discoverKeyColumns = async () => {
  const response = await axios.get(`${API}/learning/discover-key-columns`, { headers: getAuthHeader() });
  return response.data;
};

export const validateRelationship = async (sourceTable, sourceColumn, targetTable, targetColumn) => {
  const response = await axios.post(`${API}/learning/validate-relationship`, {
    source_table: sourceTable,
    source_column: sourceColumn,
    target_table: targetTable,
    target_column: targetColumn
  }, { headers: getAuthHeader() });
  return response.data;
};

// Shared Learned Corrections (visible to all admins)
export const getLearnedCorrections = async () => {
  const response = await axios.get(`${API}/learning/learned-corrections`, { headers: getAuthHeader() });
  return response.data;
};

export const saveLearnedCorrection = async (data) => {
  const response = await axios.post(`${API}/learning/learned-correction`, data, { headers: getAuthHeader() });
  return response.data;
};

export const deleteLearnedCorrection = async (database, column) => {
  const response = await axios.delete(`${API}/learning/learned-correction/${database}/${column}`, { headers: getAuthHeader() });
  return response.data;
};

// Wrong Relations - Mark relationships that AI should never use or suggest
export const saveWrongRelation = async (data) => {
  const response = await axios.post(`${API}/learning/wrong-relation`, data, { headers: getAuthHeader() });
  return response.data;
};

export const getWrongRelations = async () => {
  const response = await axios.get(`${API}/learning/wrong-relations`, { headers: getAuthHeader() });
  return response.data;
};

export const deleteWrongRelation = async (id) => {
  const response = await axios.delete(`${API}/learning/wrong-relation/${id}`, { headers: getAuthHeader() });
  return response.data;
};

// Enum Definitions (Status codes, Types, Categories - shared across all admins)
export const discoverEnumColumns = async () => {
  const response = await axios.get(`${API}/learning/discover-enum-columns`, { headers: getAuthHeader() });
  return response.data;
};

export const getEnumDefinitions = async () => {
  const response = await axios.get(`${API}/learning/enum-definitions`, { headers: getAuthHeader() });
  return response.data;
};

export const getEnumDefinition = async (server, database, table, column) => {
  const response = await axios.get(`${API}/learning/enum-definition/${server}/${database}/${table}/${column}`, { headers: getAuthHeader() });
  return response.data;
};

export const saveEnumDefinition = async (data) => {
  const response = await axios.post(`${API}/learning/enum-definition`, data, { headers: getAuthHeader() });
  return response.data;
};

export const deleteEnumDefinition = async (server, database, table, column) => {
  const response = await axios.delete(`${API}/learning/enum-definition/${server}/${database}/${table}/${column}`, { headers: getAuthHeader() });
  return response.data;
};

export const suggestEnumMeanings = async (server, database, table, column, values) => {
  const response = await axios.post(`${API}/learning/suggest-enum-meanings`, {
    server, database, table, column, values
  }, { headers: getAuthHeader() });
  return response.data;
};

export const getEnumValuesFromDB = async (server, database, table, column) => {
  const response = await axios.post(`${API}/learning/get-enum-values`, {
    server, database, table, column
  }, { headers: getAuthHeader() });
  return response.data;
};

// Table Query Patterns (for large tables - date formats, required filters)
export const getTableQueryPatterns = async () => {
  const response = await axios.get(`${API}/learning/table-query-patterns`, { headers: getAuthHeader() });
  return response.data;
};

export const getTableQueryPattern = async (server, database, table) => {
  const response = await axios.get(`${API}/learning/table-query-pattern/${server}/${database}/${table}`, { headers: getAuthHeader() });
  return response.data;
};

export const saveTableQueryPattern = async (data) => {
  const response = await axios.post(`${API}/learning/table-query-pattern`, data, { headers: getAuthHeader() });
  return response.data;
};

export const deleteTableQueryPattern = async (server, database, table) => {
  const response = await axios.delete(`${API}/learning/table-query-pattern/${server}/${database}/${table}`, { headers: getAuthHeader() });
  return response.data;
};

// AI Learning Summary
export const generateAILearningSummary = async () => {
  const response = await axios.get(`${API}/learning/ai-summary`, { headers: getAuthHeader() });
  return response.data;
};

// Global Onboarding Progress (shared across all admins)
export const getOnboardingProgress = async () => {
  const response = await axios.get(`${API}/learning/onboarding-progress`, { headers: getAuthHeader() });
  return response.data;
};

export const saveOnboardingProgress = async (currentPhase, phaseProgress) => {
  const response = await axios.post(`${API}/learning/onboarding-progress`, {
    current_phase: currentPhase,
    phase_progress: phaseProgress
  }, { headers: getAuthHeader() });
  return response.data;
};

// Mark a phase as manually complete (admin override)
export const markPhaseComplete = async (phase) => {
  const response = await axios.post(`${API}/learning/mark-phase-complete/${phase}`, {}, { headers: getAuthHeader() });
  return response.data;
};

// Remove phase override
export const removePhaseOverride = async (phase) => {
  const response = await axios.delete(`${API}/learning/phase-override/${phase}`, { headers: getAuthHeader() });
  return response.data;
};

// Learned Enum Corrections (Smart Learning)
export const getLearnedEnumCorrections = async () => {
  const response = await axios.get(`${API}/learning/learned-enum-corrections`, { headers: getAuthHeader() });
  return response.data;
};

export const saveLearnedEnumCorrection = async (data) => {
  const response = await axios.post(`${API}/learning/learned-enum-correction`, data, { headers: getAuthHeader() });
  return response.data;
};


// ========================
// BUSINESS RULES - Domain knowledge for AI
// ========================

export const getBusinessRules = async () => {
  const response = await axios.get(`${API}/learning/business-rules`, { headers: getAuthHeader() });
  return response.data;
};

export const saveBusinessRule = async (name, rule, category = 'general', examples = []) => {
  const response = await axios.post(`${API}/learning/business-rule`, {
    name, rule, category, examples
  }, { headers: getAuthHeader() });
  return response.data;
};

export const updateBusinessRule = async (ruleId, name, rule, category = 'general', examples = []) => {
  const response = await axios.put(`${API}/learning/business-rule/${ruleId}`, {
    name, rule, category, examples
  }, { headers: getAuthHeader() });
  return response.data;
};

export const deleteBusinessRule = async (ruleId) => {
  const response = await axios.delete(`${API}/learning/business-rule/${ruleId}`, { headers: getAuthHeader() });
  return response.data;
};


// ========================
// QUERY TEMPLATES/FAVORITES
// ========================

export const getQueryTemplates = async () => {
  const response = await axios.get(`${API}/query-templates`, { headers: getAuthHeader() });
  return response.data;
};

export const createQueryTemplate = async (name, query, description = '', category = 'general', isFavorite = false) => {
  const response = await axios.post(`${API}/query-templates`, {
    name, query, description, category, is_favorite: isFavorite
  }, { headers: getAuthHeader() });
  return response.data;
};

export const updateQueryTemplate = async (templateId, name, query, description, category, isFavorite) => {
  const response = await axios.put(`${API}/query-templates/${templateId}`, {
    name, query, description, category, is_favorite: isFavorite
  }, { headers: getAuthHeader() });
  return response.data;
};

export const deleteQueryTemplate = async (templateId) => {
  const response = await axios.delete(`${API}/query-templates/${templateId}`, { headers: getAuthHeader() });
  return response.data;
};

export const trackTemplateUsage = async (templateId) => {
  const response = await axios.post(`${API}/query-templates/${templateId}/use`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const toggleFavoriteTemplate = async (templateId) => {
  const response = await axios.post(`${API}/query-templates/${templateId}/favorite`, {}, { headers: getAuthHeader() });
  return response.data;
};

// ========================
// QUERY ANNOTATIONS
// ========================

export const getAnnotations = async (queryId) => {
  const response = await axios.get(`${API}/annotations/${queryId}`, { headers: getAuthHeader() });
  return response.data;
};

export const createAnnotation = async (queryId, text, annotationType = 'note') => {
  const response = await axios.post(`${API}/annotations`, {
    query_id: queryId, text, annotation_type: annotationType
  }, { headers: getAuthHeader() });
  return response.data;
};

export const deleteAnnotation = async (annotationId) => {
  const response = await axios.delete(`${API}/annotations/${annotationId}`, { headers: getAuthHeader() });
  return response.data;
};


// ========================
// SCHEDULED REPORTS
// ========================

export const getScheduledReports = async () => {
  const response = await axios.get(`${API}/scheduled-reports`, { headers: getAuthHeader() });
  return response.data;
};

export const createScheduledReport = async (name, query, schedule, scheduleTime, recipients, format = 'csv') => {
  const response = await axios.post(`${API}/scheduled-reports`, {
    name, query, schedule, schedule_time: scheduleTime, recipients, format
  }, { headers: getAuthHeader() });
  return response.data;
};

export const updateScheduledReport = async (reportId, data) => {
  const response = await axios.put(`${API}/scheduled-reports/${reportId}`, data, { headers: getAuthHeader() });
  return response.data;
};

export const deleteScheduledReport = async (reportId) => {
  const response = await axios.delete(`${API}/scheduled-reports/${reportId}`, { headers: getAuthHeader() });
  return response.data;
};

export const toggleScheduledReport = async (reportId) => {
  const response = await axios.post(`${API}/scheduled-reports/${reportId}/toggle`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const runScheduledReportNow = async (reportId) => {
  const response = await axios.post(`${API}/scheduled-reports/${reportId}/run`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const getEmailConfig = async () => {
  const response = await axios.get(`${API}/notifications/email/config`, { headers: getAuthHeader() });
  return response.data;
};

export const sendEmailNotification = async (to, subject, body, htmlBody = null) => {
  const response = await axios.post(`${API}/notifications/email`, {
    to, subject, body, html_body: htmlBody
  }, { headers: getAuthHeader() });
  return response.data;
};


// ========== KNOWLEDGE BASE: Term Mappings + Pattern Library ==========

export const listTermMappings = async () => {
  const response = await axios.get(`${API}/kb/term-mappings`, { headers: getAuthHeader() });
  return response.data;
};

export const saveTermMapping = async (mapping) => {
  // mapping: { term, server, database, table, phrases?, filter?, columns?, notes?, question? }
  const response = await axios.post(`${API}/kb/term-mappings`, mapping, { headers: getAuthHeader() });
  return response.data;
};

export const deleteTermMapping = async (mappingId) => {
  const response = await axios.delete(`${API}/kb/term-mappings/${mappingId}`, { headers: getAuthHeader() });
  return response.data;
};

export const listPatterns = async (limit = 100, adminOnly = false) => {
  const response = await axios.get(`${API}/kb/patterns`, {
    params: { limit, admin_only: adminOnly },
    headers: getAuthHeader(),
  });
  return response.data;
};

export const deletePattern = async (patternId) => {
  const response = await axios.delete(`${API}/kb/patterns/${patternId}`, { headers: getAuthHeader() });
  return response.data;
};

export const approvePattern = async (patternId) => {
  const response = await axios.post(`${API}/kb/patterns/${patternId}/approve`, {}, { headers: getAuthHeader() });
  return response.data;
};

export const findSimilarPatterns = async (question, topK = 3) => {
  const response = await axios.post(`${API}/kb/patterns/find-similar`, { question, top_k: topK }, { headers: getAuthHeader() });
  return response.data;
};

export const getKbSnapshot = async () => {
  const response = await axios.get(`${API}/kb/snapshot`, { headers: getAuthHeader() });
  return response.data;
};

// ========== KNOWLEDGE BASE: Risk Framework ==========

export const getRiskFramework = async () => {
  const response = await axios.get(`${API}/kb/risk/framework`, { headers: getAuthHeader() });
  return response.data;
};

export const updateRiskScoringRules = async (rules) => {
  const response = await axios.put(`${API}/kb/risk/scoring-rules`, rules, { headers: getAuthHeader() });
  return response.data;
};

export const createRiskTableDict = async (item) => {
  const response = await axios.post(`${API}/kb/risk/table-dict`, item, { headers: getAuthHeader() });
  return response.data;
};

export const deleteRiskTableDict = async (id) => {
  const response = await axios.delete(`${API}/kb/risk/table-dict/${id}`, { headers: getAuthHeader() });
  return response.data;
};

export const createRiskMetric = async (item) => {
  const response = await axios.post(`${API}/kb/risk/metric`, item, { headers: getAuthHeader() });
  return response.data;
};

export const deleteRiskMetric = async (id) => {
  const response = await axios.delete(`${API}/kb/risk/metric/${id}`, { headers: getAuthHeader() });
  return response.data;
};

export const createRiskExample = async (item) => {
  const response = await axios.post(`${API}/kb/risk/example`, item, { headers: getAuthHeader() });
  return response.data;
};

export const deleteRiskExample = async (id) => {
  const response = await axios.delete(`${API}/kb/risk/example/${id}`, { headers: getAuthHeader() });
  return response.data;
};
